/**
 * The office's view of the vans' phones: which phone sells for which van,
 * when each last synced, and every document they sent -- above all the
 * ones the books could not take, which were printed for a customer and so
 * must be resolved, never ignored. A lost phone is switched off here.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Paper,
  Skeleton, Stack, Tab, Tabs, Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { fetchVanDocs, fetchVanPhones, retryVanDoc, revokeVanPhone } from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P, type VanPhone } from '../api/types'
import { useAuth } from '../store/AuthContext'
import ScreenTitle from '../components/ScreenTitle'
import { c, fmtMoney } from '../theme'

const when = (iso?: string | null, lang = 'en') => (iso
  ? new Date(iso + (iso.endsWith('Z') ? '' : 'Z')).toLocaleString(lang === 'ar' ? 'ar' : 'en-GB',
    { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—')

export default function VanPhones() {
  const { t, i18n } = useTranslation()
  const nav = useNavigate()
  const qc = useQueryClient()
  const { can } = useAuth()
  const [tab, setTab] = useState<'FAILED' | 'ALL'>('FAILED')
  const [revoking, setRevoking] = useState<VanPhone | null>(null)
  const phones = useQuery({ queryKey: ['van-phones'], queryFn: fetchVanPhones })
  const docs = useQuery({ queryKey: ['van-docs', tab],
                          queryFn: () => fetchVanDocs(tab === 'FAILED' ? { status: 'FAILED' } : {}) })
  const retry = useMutation({
    mutationFn: (id: number) => retryVanDoc(id),
    onSettled: () => { void qc.invalidateQueries({ queryKey: ['van-docs'] }) },
  })
  const revoke = useMutation({
    mutationFn: (id: number) => revokeVanPhone(id),
    onSuccess: () => { setRevoking(null); void qc.invalidateQueries({ queryKey: ['van-phones'] }) },
  })
  const manage = can(P.inventoryMasters)
  const lang = i18n.language

  return (
    <Stack spacing={2}>
      <Paper variant="outlined">
        <ScreenTitle titleKey="nav.vanPhones" sub={t('van.phonesSub')}
          right={can(P.inventoryVan) ? (
            <Button variant="contained" onClick={() => nav('/van')}>{t('van.openVan')}</Button>
          ) : undefined} />
        {phones.isLoading ? <Box sx={{ p: 2 }}><Skeleton /><Skeleton /></Box>
          : phones.isError ? <Alert severity="warning" sx={{ m: 2 }}>{errorMessage(phones.error)}</Alert>
            : (phones.data?.devices.length ?? 0) === 0 ? (
              <Typography sx={{ p: 3, color: c.inkMuted }}>{t('van.noPhones')}</Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <table className="rpt" style={{ minWidth: 720 }}>
                  <thead><tr>
                    <th>{t('inv.kindVAN')}</th><th>{t('inv.salesman')}</th><th>{t('van.phone')}</th>
                    <th style={{ width: 150 }}>{t('van.lastSync')}</th><th style={{ width: 130 }} />
                  </tr></thead>
                  <tbody>
                    {phones.data!.devices.map((d) => {
                      const off = !!d.revoked_at
                      const stale = !off && (!d.last_sync_at || Date.now() - new Date(d.last_sync_at + 'Z').getTime()
                        > (phones.data!.max_offline_days - 2) * 86_400_000)
                      return (
                        <tr key={d.id} style={{ opacity: off ? 0.55 : 1 }}>
                          <td><span className="doc-no">{d.store_code}</span> · {d.store_name}</td>
                          <td>{d.salesman ?? '—'}</td>
                          <td>{d.device_name}</td>
                          <td style={{ color: stale ? 'var(--warning-ink)' : undefined }}>{when(d.last_sync_at, lang)}</td>
                          <td style={{ textAlign: 'end' }}>
                            {off ? <Chip size="small" label={t(d.revoked_reason === 'TAKEOVER'
                              ? 'van.replaced' : 'van.switchedOff')} />
                              : manage && <Button size="small" color="error"
                                onClick={() => setRevoking(d)}>{t('van.switchOff')}</Button>}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </Box>
            )}
      </Paper>

      <Paper variant="outlined">
        <Stack direction="row" alignItems="center" sx={{ px: 2.5, pt: 1.5 }} spacing={2}>
          <Typography component="h2" sx={{ m: 0, fontSize: 15, fontWeight: 600, flex: 1 }}>
            {t('van.fromThePhones')}</Typography>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}
            sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0 } }}>
            <Tab value="FAILED" label={t('van.needsAttention')} />
            <Tab value="ALL" label={t('van.everything')} />
          </Tabs>
        </Stack>
        {docs.isLoading ? <Box sx={{ p: 2 }}><Skeleton /></Box>
          : (docs.data?.documents.length ?? 0) === 0 ? (
            <Typography sx={{ p: 3, color: c.inkMuted }}>
              {tab === 'FAILED' ? t('van.nothingFailed') : t('van.noDocsYet')}</Typography>
          ) : (
            <Box sx={{ overflowX: 'auto', pt: 1 }}>
              <table className="rpt" style={{ minWidth: 820 }}>
                <thead><tr>
                  <th style={{ width: 150 }}>{t('van.issued')}</th>
                  <th style={{ width: 170 }}>{t('report.voucherNo')}</th>
                  <th>{t('inv.kindVAN')}</th>
                  <th className="num" style={{ width: 110 }}>{t('inv.total')}</th>
                  <th>{t('van.result')}</th>
                  <th style={{ width: 100 }} />
                </tr></thead>
                <tbody>
                  {docs.data!.documents.map((x) => (
                    <tr key={x.id} className={x.sale_id ? 'clickable' : undefined}
                      onClick={() => x.sale_id && nav(`/inventory/sales/${x.sale_id}`)}>
                      <td style={{ whiteSpace: 'nowrap' }}>{when(x.issued_at, lang)}</td>
                      <td><span className="doc-no">{x.doc_no}</span></td>
                      <td>{x.store_name}</td>
                      <td className="num">{fmtMoney(x.total)}</td>
                      <td style={{ fontSize: 13 }}>
                        {x.status === 'FAILED'
                          ? <span style={{ color: 'var(--danger-ink)' }}>{x.error}</span>
                          : x.warnings.length ? <span style={{ color: 'var(--warning-ink)' }}>{x.warnings.join(' · ')}</span>
                            : <span style={{ color: 'var(--ink-muted)' }}>{t('van.posted')}</span>}
                      </td>
                      <td style={{ textAlign: 'end' }}>
                        {x.status === 'FAILED' && manage && (
                          <Button size="small" disabled={retry.isPending}
                            onClick={(e) => { e.stopPropagation(); retry.mutate(x.id) }}>{t('van.retry')}</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}
        <Typography sx={{ px: 2.5, py: 2, fontSize: 12.5, color: c.inkMuted }}>{t('van.docsNote')}</Typography>
      </Paper>

      <Dialog open={!!revoking} onClose={() => setRevoking(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('van.switchOff')}</DialogTitle>
        <DialogContent>
          {revoke.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(revoke.error)}</Alert>}
          <Typography>{t('van.switchOffBody', { phone: revoking?.device_name, van: revoking?.store_name })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setRevoking(null)}>{t('common.close')}</Button>
          <Button color="error" variant="contained" disabled={revoke.isPending}
            onClick={() => revoking && revoke.mutate(revoking.id)}>{t('van.switchOff')}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}
